import { Link, useLocation } from "@/lib/router-compat";
import SEOHead from "@/components/SEOHead";
import { Home, Briefcase, FolderOpen, Mail, HelpCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { HELP } from "@/lib/help";

const NotFound = () => {
  const location = useLocation();
  const suggestedPages = [
    {
      icon: Briefcase,
      title: "Services",
      description: "Explore our web development services",
      href: "/services",
    },
    {
      icon: FolderOpen,
      title: "Projects",
      description: "View our recent work and case studies",
      href: "/projects",
    },
    {
      icon: Mail,
      title: "Contact",
      description: "Get in touch with our team",
      href: "/contact",
    },
    {
      icon: HelpCircle,
      title: "FAQ",
      description: "Find answers to common questions",
      href: "/faq",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead title="Page Not Found" description="The requested NETIUS page could not be found." path={location.pathname} noindex />
      <Header />
      
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-16">
        {/* 404 Heading */}
        <h1 className="text-[120px] md:text-[180px] font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-600 leading-none">
          404
        </h1>
        
        {/* Divider */}
        <div className="w-24 h-1 bg-primary my-6" />
        
        {/* Message */}
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
          Page Not Found
        </h2>
        <p className="text-muted-foreground text-center max-w-md mb-8">
          The page you're looking for doesn't exist or has been moved. Let's get you back on track.
        </p>
        
        {/* Back to Home Button */}
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-8 py-3 text-sm font-medium text-foreground hover:bg-muted transition-all duration-300"
        >
          <Home className="h-4 w-4" />
          Back to Home
        </Link>
        
        {/* Divider */}
        <div className="w-full max-w-4xl border-t border-border my-12" />
        
        {/* Suggested Pages */}
        <p className="text-muted-foreground mb-8">Or try one of these pages:</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full max-w-4xl">
          {suggestedPages.map((page) => (
            <Link
              key={page.title}
              to={page.href}
              className="group p-6 rounded-lg border border-border bg-card hover:bg-muted transition-all duration-300"
            >
              <div className="flex items-start gap-3">
                <page.icon className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {page.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {page.description}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        {/* Contact Link */}
        <p className="text-muted-foreground mt-12">
          Still can't find what you're looking for?{" "}
          <Link to="/contact" className="text-foreground underline hover:text-primary transition-colors">
            Contact us
          </Link>{" "}
          and we'll help you out.
        </p>

        {/* HELP prompt */}
        <p className="text-sm text-muted-foreground mt-4">
          Lost?{" "}
          <a
            href={HELP.knowledgeBase}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground underline hover:text-primary transition-colors"
          >
            Search our Knowledge Base
          </a>{" "}
          or{" "}
          <a
            href={HELP.submitTicket}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground underline hover:text-primary transition-colors"
          >
            submit a support request
          </a>
          .
        </p>
      </main>
      
      <Footer />
    </div>
  );
};

export default NotFound;
